import { Ingredient, MeasuredIngredient } from "../models/ingredient";
import { MethodStep } from "../models/method-step";
import { getQuantityString } from "./units";
import {
  parseIngredientLineWithSubstitute,
  matchIngredientGroup,
  matchMethodGroup,
} from "./parseIngredientLine";
import { generateId } from "./generateId";

// Round-trip helpers for the per-section "Bulk edit" text boxes. Ingredients are
// one-per-line ("# Heading" marks a group, " | " separates a substitute);
// method steps are separated by a blank line so a step can span multiple
// (single-break) lines.

// --- Ingredients -----------------------------------------------------------

function ingredientLineText(ing: MeasuredIngredient & { name: string }): string {
  // Free-text quantity ("to taste") reads better after the name than before.
  if (ing.other) return [ing.name, ing.other].filter(Boolean).join(" ").trim();
  return [getQuantityString(ing), ing.name].filter(Boolean).join(" ").trim();
}

export function ingredientsToText(ingredients: Ingredient[]): string {
  return ingredients
    .map((ing) => {
      if (ing.group) return `# ${ing.name}`.trim();
      return [ingredientLineText(ing), ing.substitute ? ingredientLineText(ing.substitute) : null]
        .filter(Boolean)
        .join(" | ");
    })
    .join("\n");
}

export function textToIngredients(text: string, recipeId: string): Ingredient[] {
  return text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .map((line, index) => {
      // Group heading in any supported form (# Sauce · [Sauce] · Sauce: · For …).
      const heading = matchIngredientGroup(line);
      if (heading !== null) {
        return {
          id: generateId(),
          recipeId,
          name: heading,
          group: true,
          quantity: null,
          fraction: null,
          unitType: null,
          unit: null,
          other: null,
          sortOrder: index,
        } as Ingredient;
      }
      // "main | substitute" is handled by the shared pipe-aware parser, the
      // same syntax ingredientsToText emits.
      const parsed = parseIngredientLineWithSubstitute(line);
      return {
        id: generateId(),
        recipeId,
        name: parsed.name,
        group: parsed.group ?? false,
        quantity: parsed.quantity ?? null,
        fraction: parsed.fraction ?? null,
        unitType: parsed.unitType ?? null,
        unit: parsed.unit ?? null,
        other: parsed.other ?? null,
        substitute: parsed.substitute ?? null,
        sortOrder: index,
      } as Ingredient;
    });
}

// --- Method ----------------------------------------------------------------

export function methodStepsToText(steps: MethodStep[]): string {
  return steps
    .map((s) => {
      // Section headings serialise as "# Heading" (a single-line block), the same
      // marker ingredient groups use, so they re-parse as headings on the way back.
      if (s.group) return `# ${(s.instruction ?? "").trim()}`.trim();
      // Collapse any blank lines *within* a step so the blank line is unambiguously
      // the step separator; a step can still span multiple single-break lines.
      return (s.instruction ?? "").replace(/\n[ \t]*\n+/g, "\n").trim();
    })
    .filter(Boolean)
    .join("\n\n");
}

export function textToMethodSteps(text: string, recipeId: string): MethodStep[] {
  return text
    .split(/\n[ \t]*\n+/) // blank line = step break
    .map((block) => block.trim())
    .filter(Boolean)
    .map((block, index) => {
      // A block written as a heading (# Sauce · [Sauce] · short "Sauce:") becomes
      // a group row; everything else is a normal step.
      const heading = matchMethodGroup(block);
      return {
        id: generateId(),
        recipeId,
        stepNumber: index + 1,
        instruction: heading ?? block,
        group: heading !== null,
      } as MethodStep;
    });
}
